import { CHANNEL_META } from './hero.constants';
import type { HeroContent, HeroLink } from './hero.types';

/** One full-viewport slide in the hero carousel. */
export interface HeroSlide {
  id: string;
  eyebrow?: string;
  headline: string;
  /** Substring of `headline` to wrap in the animated brand sheen. */
  highlight?: string;
  description: string;
  primaryCta: HeroLink;
  secondaryCta?: HeroLink;
  /** Per-slide radial wash so slides read as distinct scenes. */
  tint: string;
}

/**
 * Build the carousel slides from the (CMS-derived) hero content. Slide 1 is the
 * CMS hero verbatim; the follow-ups reframe the same product around channels and
 * outcomes so the reel stays truthful — layout borrowed from full-screen hero
 * carousels, all copy/brand is Vaanexa's own.
 */
export function buildHeroSlides(content: HeroContent): HeroSlide[] {
  const channelNames = content.enabledChannels
    .map((id) => CHANNEL_META[id].label)
    .join(', ')
    .replace(/, ([^,]*)$/, ' & $1');

  return [
    {
      id: 'intro',
      eyebrow: content.eyebrow,
      headline: content.headline,
      highlight: content.highlightedText,
      description: content.description,
      primaryCta: content.primaryCta,
      secondaryCta: content.secondaryCta,
      tint: 'radial-gradient(75% 55% at 72% 6%, rgba(122,59,255,0.20) 0%, transparent 58%), radial-gradient(50% 45% at 90% 26%, rgba(217,154,43,0.16) 0%, transparent 55%)',
    },
    {
      id: 'channels',
      eyebrow: 'One inbox, every channel',
      headline: `${channelNames}, together.`,
      highlight: channelNames,
      description:
        'Bring every business conversation into a single inbox — no tab-switching, no missed messages, replies in seconds.',
      primaryCta: content.primaryCta,
      secondaryCta: { label: 'See channels', href: '/channels/whatsapp' },
      tint: 'radial-gradient(70% 55% at 26% 8%, rgba(37,211,102,0.16) 0%, transparent 58%), radial-gradient(55% 45% at 88% 30%, rgba(225,48,108,0.12) 0%, transparent 55%)',
    },
    {
      id: 'outcomes',
      eyebrow: 'Conversation to conversion',
      headline: 'From first message to paid customer.',
      highlight: 'paid customer',
      description:
        'Qualify leads, book appointments, automate follow-ups and take payments — all inside the chat your customers already use.',
      primaryCta: content.primaryCta,
      secondaryCta: { label: 'How it works', href: '/product' },
      tint: 'radial-gradient(72% 55% at 74% 8%, rgba(52,211,153,0.16) 0%, transparent 58%), radial-gradient(52% 45% at 10% 90%, rgba(124,91,214,0.14) 0%, transparent 55%)',
    },
  ];
}
